import { forwardRef, Ref, useState, ReactElement, ChangeEvent, useEffect } from 'react';
import {
  Avatar,
  Link,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  lighten,
  List,
  ListItem,
  ListItemAvatar,
  TextField,
  Theme,
  Tooltip,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  Slide,
  Hidden
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import { NavLink as RouterLink } from 'react-router-dom';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import ChevronRightTwoToneIcon from '@mui/icons-material/ChevronRightTwoTone';
import menuConfig from 'src/config/menu';
import { passesMenuAccessGate, getNavPermissionContext } from 'src/Common/permissions/cloudPosPermissions';


const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement<any, any> },
  ref: Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DialogWrapper = styled(Dialog)(
  () => `
    .MuiDialog-container {
        height: auto;
    }
    
    .MuiDialog-paperScrollPaper {
        max-height: calc(100vh - 64px)
    }
`
);

const SearchInputWrapper = styled(TextField)(
  ({ theme }) => `
    background: ${theme.colors.alpha.white[100]};

    .MuiInputBase-input {
        font-size: ${theme.typography.pxToRem(17)};
    }
`
);

const DialogTitleWrapper = styled(DialogTitle)(
  ({ theme }) => `
    background: ${theme.colors.alpha.black[5]};
    padding: ${theme.spacing(3)}
`
);

function HeaderSearch() {
  const [openSearchResults, setOpenSearchResults] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [filteredResults, setFilteredResults] = useState<any[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const storedRoles = localStorage.getItem('userRoles') || '';
    setRoles(storedRoles.split(',').map(role => role.trim()));
  }, []);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value.toLowerCase();
    setSearchValue(value);

    if (value) {
      const { lookup, bypass } = getNavPermissionContext();
      const flatMenu = menuConfig.flatMap(group => group.items);
      const results = flatMenu.filter(item =>
        passesMenuAccessGate(item, roles, lookup, bypass) &&
        (item.title.toLowerCase().includes(value) || item.path.toLowerCase().includes(value))
      );
      setFilteredResults(results);
      setOpenSearchResults(results.length > 0);
    } else {
      setOpenSearchResults(false);
      setFilteredResults([]);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchValue('');
    setFilteredResults([]);
  };

  return (
    <>
      <Tooltip arrow title="Search">
        <IconButton color="primary" onClick={handleClickOpen}>
          <SearchTwoToneIcon />
        </IconButton>
      </Tooltip>

      <DialogWrapper
        open={open}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="md"
        fullWidth
        scroll="paper"
        onClose={handleClose}
      >
        <DialogTitleWrapper>
          <SearchInputWrapper
            value={searchValue}
            autoFocus={true}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchTwoToneIcon />
                </InputAdornment>
              )
            }}
            placeholder="Search terms here..."
            fullWidth
            label="Search"
          />
        </DialogTitleWrapper>
        <Divider />

        {openSearchResults && (
          <DialogContent>
            <Box
              sx={{ pt: 0, pb: 1 }}
              display="flex"
              justifyContent="space-between"
            >
              <Typography variant="body2" component="span">
                Search results for{' '}
                <Typography
                  sx={{ fontWeight: 'bold' }}
                  variant="body1"
                  component="span"
                >
                  {searchValue}
                </Typography>
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <List disablePadding>
              {filteredResults.map((item) => (
                <div key={item.path}>
                  <ListItem 
                    button 
                    component={RouterLink} 
                    to={item.path}
                    onClick={handleClose}
                  >
                    <Hidden smDown>
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            background: (theme: Theme) =>
                              theme.palette.secondary.main
                          }}
                        >
                          {item.icon}
                        </Avatar>
                      </ListItemAvatar>
                    </Hidden>
                    <Box flex="1">
                      <Box display="flex" justifyContent="space-between">
                        <Link
                          component={RouterLink}
                          to={item.path}
                          underline="hover"
                          sx={{ fontWeight: 'bold' }}
                          variant="body2"
                        >
                          {item.title}
                        </Link>
                      </Box>
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          color: (theme: Theme) =>
                            lighten(theme.palette.secondary.main, 0.5)
                        }}
                      >
                        {item.path}
                      </Typography>
                    </Box>
                    <ChevronRightTwoToneIcon />
                  </ListItem>
                  <Divider sx={{ my: 1 }} component="li" />
                </div>
              ))}
            </List>
            {filteredResults.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2">No results found</Typography>
              </Box>
            )}
          </DialogContent>
        )}
      </DialogWrapper>
    </>
  );
}

export default HeaderSearch;